import type { DeviceTypeFieldDefinition, ReportedConfigStatus, SecretFieldState } from "../../api/types";
import {
  objectArrayDraft,
  objectArrayValue,
  stringifyFieldValue,
  type ConfigValues,
  type ObjectArrayItemDraft,
} from "../../components/config/configForm";

// Pure helpers behind the Teltonika gateway settings page.
//
// The gateway differs from the two-way comms board in one way that shapes all
// of this: it REPORTS its full running configuration, and a change made on its
// own web page is adopted as its override (local wins). So the form layers
// override -> reported -> default, an empty string is a real value rather than
// "unset", and a save sends the complete configuration, because every key it
// leaves out would simply stay at whatever the device already runs.

export const TELTONIKA_GATEWAY_TYPE_ID = "teltonika_gateway";
export const EYE_ENTRIES_PATH = "ble.entries";

const MAC_PATTERN = /^[0-9A-F]{2}(:[0-9A-F]{2}){5}$/;

export function usesGatewayConfigPage(deviceTypeId?: string | null): boolean {
  return deviceTypeId === TELTONIKA_GATEWAY_TYPE_ID;
}

// readAtPath distinguishes "absent" from a present empty string: on the
// gateway "" is a value (no MQTT topic), not a gap to fill from a default.
export function readAtPath(source: Record<string, unknown> | undefined | null, path: string): { found: boolean; value: unknown } {
  let current: unknown = source;
  for (const part of path.split(".")) {
    if (!current || typeof current !== "object" || Array.isArray(current) || !(part in (current as Record<string, unknown>))) {
      return { found: false, value: undefined };
    }
    current = (current as Record<string, unknown>)[part];
  }
  if (current === null || current === undefined) return { found: false, value: undefined };
  return { found: true, value: current };
}

function setAtPath(target: Record<string, unknown>, path: string, value: unknown): void {
  const parts = path.split(".");
  let current = target;
  for (const part of parts.slice(0, -1)) {
    const next = current[part];
    if (!next || typeof next !== "object" || Array.isArray(next)) current[part] = {};
    current = current[part] as Record<string, unknown>;
  }
  current[parts[parts.length - 1]] = value;
}

// layeredConfigValues seeds form state from the first source that HAS each
// path (override, then reported, then the schema default), empty strings
// included. object_array fields are skipped: see layeredEntries.
export function layeredConfigValues(
  fields: DeviceTypeFieldDefinition[],
  sources: Array<Record<string, unknown> | undefined | null>,
): ConfigValues {
  const out: ConfigValues = {};
  for (const field of fields) {
    if (field.kind === "object_array") continue;
    const hit = sources.map((source) => readAtPath(source, field.path)).find((entry) => entry.found);
    if (hit) {
      out[field.path] = stringifyFieldValue(field, hit.value);
    } else {
      out[field.path] = field.kind === "boolean" ? "false" : "";
    }
  }
  return out;
}

// reportedConfigValues is the device's side in form-state form, holding only
// the paths it reported - so "On device" is shown only where there is one.
export function reportedConfigValues(
  fields: DeviceTypeFieldDefinition[],
  reported: Record<string, unknown> | undefined | null,
): ConfigValues {
  const out: ConfigValues = {};
  for (const field of fields) {
    if (field.kind === "object_array") continue;
    const hit = readAtPath(reported, field.path);
    if (hit.found) out[field.path] = stringifyFieldValue(field, hit.value);
  }
  return out;
}

export function layeredEntries(
  field: DeviceTypeFieldDefinition,
  sources: Array<Record<string, unknown> | undefined | null>,
): ObjectArrayItemDraft[] {
  const hit = sources.map((source) => readAtPath(source, field.path)).find((entry) => entry.found);
  return hit ? objectArrayDraft(field, hit.value) : [];
}

// macWarning checks one MAC list (one per line) the way the gateway will: it
// upper-cases, then refuses anything that is not AA:BB:CC:DD:EE:FF - and one
// bad MAC makes it refuse the whole sensor list.
export function macWarning(raw: string): string | null {
  const bad = raw
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((mac) => !MAC_PATTERN.test(mac.toUpperCase()));
  if (bad.length === 0) return null;
  return `Not a MAC address (AA:BB:CC:DD:EE:FF): ${bad.join(", ")}. The gateway refuses the whole sensor list if any MAC is malformed.`;
}

// normaliseEyeEntries upper-cases MACs, as the gateway does before storing and
// reporting them. Sending lower case would leave desired != reported forever.
export function normaliseEyeEntries(entries: Array<Record<string, unknown>>): Array<Record<string, unknown>> {
  return entries.map((entry) => ({
    ...entry,
    macs: Array.isArray(entry.macs) ? entry.macs.map((mac) => String(mac).toUpperCase()) : entry.macs,
  }));
}

// buildGatewayOverrides builds the complete override a save sends (PATCH
// replaces the stored override). Every schema field is written, empty strings
// included, so desired matches what the gateway reports; anything else is
// dropped so a key the schema no longer knows cannot fail the save.
//
// That includes the FOTA client's own top-level checkInMinutes. The gateway
// sets its check-in cadence itself from checkin.*, the platform refuses
// checkInMinutes for this type, and leaving one stored before that out of the
// save removes it from the override - which makes iot-config-pusher clear it
// from desired.
//
// Secret fields are the exception: one is sent only when the operator typed a
// new value (it is in `touched` and not empty), exactly as typed. Leaving it
// out keeps the stored secret on the server, so an unrelated save never
// resends - or, for a caller who cannot read it, blanks - the password.
export function buildGatewayOverrides(
  fields: DeviceTypeFieldDefinition[],
  values: ConfigValues,
  entries: ObjectArrayItemDraft[],
  touched: ReadonlySet<string> = new Set(),
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const field of fields) {
    if (field.deprecated) continue;
    if (field.kind === "object_array") {
      if (field.path === EYE_ENTRIES_PATH) {
        setAtPath(out, field.path, normaliseEyeEntries(objectArrayValue(field, entries)));
      }
      continue;
    }
    const raw = values[field.path] ?? "";
    switch (field.kind) {
      case "number": {
        const trimmed = raw.trim();
        // A blank number is left out: the device keeps what it has.
        if (trimmed !== "" && Number.isFinite(Number(trimmed))) setAtPath(out, field.path, Number(trimmed));
        break;
      }
      case "boolean":
        setAtPath(out, field.path, raw === "true");
        break;
      case "secret":
        if (touched.has(field.path) && raw !== "") setAtPath(out, field.path, raw);
        break;
      case "string_array":
      case "phone_array":
        setAtPath(
          out,
          field.path,
          raw.split("\n").map((item) => item.trim()).filter(Boolean),
        );
        break;
      default:
        setAtPath(out, field.path, raw.trim());
    }
  }
  return out;
}

// limitOverridesToPaths trims a built override to the fields the operator
// touched, plus those the stored override already sets.
//
// Used before the gateway has ever reported. The form is then seeded from the
// contract defaults - no server, no sensors - and sending all of it would write
// those defaults over a gateway already set up on its own web page, and stop its
// first report from becoming the baseline. Only what the operator chose goes.
export function limitOverridesToPaths(
  built: Record<string, unknown>,
  existing: Record<string, unknown> | undefined,
  fields: DeviceTypeFieldDefinition[],
  touched: ReadonlySet<string>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const field of fields) {
    if (field.deprecated) continue;
    if (!touched.has(field.path) && !readAtPath(existing, field.path).found) continue;
    const hit = readAtPath(built, field.path);
    if (hit.found) setAtPath(out, field.path, hit.value);
  }
  return out;
}

// omitUnreportedPaths drops from a built override every field the gateway does
// not report, unless the operator touched it or the stored override already
// sets it.
//
// Used once the gateway has reported. Firmware that predates a key (e.g.
// checkin.* and restart.*, schema version 3) does not report it and ignores it,
// so writing the form's default for it would leave a permanent delta in the
// shadow - re-sent at every check-in - and a pending change the operator never
// made. A key the operator sets on purpose is still sent.
export function omitUnreportedPaths(
  built: Record<string, unknown>,
  reported: Record<string, unknown> | undefined | null,
  existing: Record<string, unknown> | undefined,
  fields: DeviceTypeFieldDefinition[],
  touched: ReadonlySet<string>,
): Record<string, unknown> {
  const out = JSON.parse(JSON.stringify(built)) as Record<string, unknown>;
  for (const field of fields) {
    if (readAtPath(reported, field.path).found) continue;
    if (touched.has(field.path) || readAtPath(existing, field.path).found) continue;
    deleteAtPath(out, field.path);
  }
  return out;
}

// deleteAtPath removes one dotted path, and any parent object it leaves empty.
function deleteAtPath(target: Record<string, unknown>, path: string): void {
  const parts = path.split(".");
  const chain: Array<Record<string, unknown>> = [target];
  for (const part of parts.slice(0, -1)) {
    const next = chain[chain.length - 1][part];
    if (!next || typeof next !== "object" || Array.isArray(next)) return;
    chain.push(next as Record<string, unknown>);
  }
  delete chain[chain.length - 1][parts[parts.length - 1]];
  for (let index = chain.length - 1; index > 0; index -= 1) {
    if (Object.keys(chain[index]).length > 0) break;
    delete chain[index - 1][parts[index - 1]];
  }
}

// stableStringify is JSON with object keys sorted, so two payloads that differ
// only in key order (DynamoDB round trips do not preserve it) compare equal.
export function stableStringify(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  if (value && typeof value === "object") {
    const entries = Object.keys(value as Record<string, unknown>)
      .sort((a, b) => a.localeCompare(b))
      .map((key) => `${JSON.stringify(key)}:${stableStringify((value as Record<string, unknown>)[key])}`);
    return `{${entries.join(",")}}`;
  }
  return JSON.stringify(value);
}

// pendingPaths lists the schema paths where the saved override (desired) is not
// what the device reported: changes it has not taken yet, or refused. A secret
// is judged by the API's own comparison (`secrets`), because a caller who may
// not read it receives neither value.
export function pendingPaths(
  fields: DeviceTypeFieldDefinition[],
  overrides: Record<string, unknown> | undefined,
  reported: Record<string, unknown> | undefined | null,
  secrets?: Record<string, SecretFieldState>,
): string[] {
  const out: string[] = [];
  for (const field of fields) {
    if (field.deprecated) continue;
    const secret = field.kind === "secret" ? secrets?.[field.path] : undefined;
    if (secret) {
      if (secret.pending) out.push(field.path);
      continue;
    }
    const desired = readAtPath(overrides, field.path);
    if (!desired.found) continue;
    const actual = readAtPath(reported, field.path);
    if (!actual.found || stableStringify(desired.value) !== stableStringify(actual.value)) out.push(field.path);
  }
  return out;
}

// outOfRangeFields finds number values outside the schema's range that the
// operator is about to send. The gateway would refuse them (keeping its value
// and leaving a permanent pending change), so the page stops the save. A value
// the device itself reports - e.g. port 0 on an unconfigured gateway - is not
// the operator's doing and does not block unrelated edits.
export function outOfRangeFields(
  fields: DeviceTypeFieldDefinition[],
  values: ConfigValues,
  deviceValues: ConfigValues,
): DeviceTypeFieldDefinition[] {
  return fields.filter((field) => {
    if (field.kind !== "number") return false;
    const raw = (values[field.path] ?? "").trim();
    if (raw === "" || !Number.isFinite(Number(raw))) return false;
    if (deviceValues[field.path] !== undefined && deviceValues[field.path] === raw) return false;
    const n = Number(raw);
    return (field.min !== undefined && n < field.min) || (field.max !== undefined && n > field.max);
  });
}

export function describeSource(source: ReportedConfigStatus["source"] | undefined): string {
  switch (source) {
    case "local":
      return "Changed on the gateway itself";
    case "cloud":
      return "Applied from FOTA";
    case "boot":
      return "Reported at start-up";
    default:
      return "Unknown";
  }
}

// Which gateway fields apply given the rest of the form: MQTT settings only
// while that server's protocol is MQTT, and the secondary server's details
// only when it is not disabled. Hidden fields keep their values and are still
// saved, so switching back shows what was there.
export function gatewayFieldVisible(field: DeviceTypeFieldDefinition, values: ConfigValues): boolean {
  const mqtt = /^(server2?)\.mqtt\./.exec(field.path);
  if (mqtt && values[`${mqtt[1]}.protocol`] !== "mqtt") return false;
  if (field.path.startsWith("server2.") && field.path !== "server2.mode" && values["server2.mode"] === "disabled") {
    return false;
  }
  return true;
}
